import * as Sentry from '@sentry/browser'

export default () => {
  const userStore = useUserStore()
  const callStore = useCallStore()
  const { getEffectiveAuthToken } = useEffectiveAuthToken()

  let abortController: AbortController | null = null

  const handleEvent = (eventType: string, eventData: string) => {
    if (!eventData) { return }
    try {
      let payload = JSON.parse(eventData)
      if (typeof payload === 'string') {
        payload = JSON.parse(payload)
      }
      // eslint-disable-next-line no-console
      console.log(`Received event of type ${eventType} with payload:`, payload)
      if (eventType === 'call_update') {
        const callStore = useCallStore()
        callStore.processEventStream(payload)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const waitForLoginId = (): Promise<string | null> => {
    const id = userStore.readable.userInfo?.id
    if (id) { return Promise.resolve(id) }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        unwatch()
        resolve(null)
      }, 5000)

      const unwatch = watch(
        () => userStore.readable.userInfo?.id,
        (newId) => {
          if (newId) {
            clearTimeout(timeout)
            unwatch()
            resolve(newId)
          }
        }
      )
    })
  }

  const connect = async () => {
    const loginId = await waitForLoginId()
    if (!loginId) { return }
    const authToken = await getEffectiveAuthToken()
    if (!authToken) { return }
    abortController = new AbortController()
    try {
      // We need to use fetch() because it gave us access to the raw response stream.
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}api/events/?login_id=${loginId}`, {
        signal: abortController.signal,
        headers: {
          Authorization: authToken,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache'
        }
      })
      if (!response.ok || !response.body) {
        throw new Error(`Failed to connect to event stream: ${response.status}`)
      }
      callStore.isEventStreamConnected = true
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let eventType = 'message'
      let eventData = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) { break }
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            eventData += (eventData ? '\n' : '') + line.slice(5).trim()
          } else if (line.trim() === '') {
            handleEvent(eventType, eventData)
            eventType = 'message'
            eventData = ''
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') { return }
      Sentry.captureException(error)
    } finally {
      callStore.isEventStreamConnected = false
    }
  }

  const disconnect = () => {
    abortController?.abort()
    abortController = null
  }

  return { connect, disconnect }
}
